/**
 * Product slug → public route, where the two differ.
 *
 * The CMS product table (`cms_products`) is the source of truth for the
 * product LINE-UP, but a row's `slug` is an internal identifier that does
 * not always match the URL its page ships at.
 *
 * `ai-advisor` is the CMS row for SmartCindy — the row predates the brand
 * name and still carries the internal slug. The public page ships at
 * /products/smart-cindy, so a card that links to `/products/${slug}` would
 * point at a URL no route matches, which renders the 404 page. Both the
 * homepage grid and the /products listing link their cards from the CMS
 * slug, so the mapping lives here and both read it.
 *
 * `bite-learn` is the opposite case and needs no entry: it IS served at
 * /products/bite-learn/, because it is a standalone static HTML page
 * (public/products/bite-learn/) rather than a React route. It needs a
 * plain <a> instead of <Link> so the browser makes a real request — see
 * the `isExternalStaticPage` branch in each consumer.
 *
 * `national-education` was the same case until 2026-11-03, when it became
 * a React route (NationalEducationPage) so that it could carry the site
 * navbar and footer. Its card now goes through <Link> like every other
 * product, and the route is locale-aware.
 *
 * As of 2026-11-03 every CMS product slug has a public route of its own:
 * `ai-advisor` maps to /products/smart-cindy, `national-education` and
 * `bite-learn` ship at their slug paths, and the five products that used
 * to have no page at all — hackathon, exploration-trip, mentorship,
 * flash-cards and claw-machine — now render from ProductDetailPage. So
 * the fallback below (`/products/${slug}`) is the correct URL for every
 * product except `ai-advisor`.
 */

import { safeExternalUrl } from '@/utils/safeExternalUrl'

/** The public route for a CMS product slug, when it differs from the slug. */
export const PRODUCT_ROUTES: Record<string, string> = {
  'ai-advisor': '/products/smart-cindy',
}

/**
 * Products whose call to action leaves THIS site.
 *
 * `csp` (升學及職涯起航平台) runs on its own website, https://csp.yada.hk.
 * It still has a page on this site — /products/csp — so its card TITLE
 * links there (the on-site description) while the card's BUTTON goes out
 * to the product's own site, which is where the sign-up actually happens.
 *
 * The stored URL is validated through `safeExternalUrl` before it reaches
 * an href: only http/https survives, so a `javascript:` or a
 * protocol-relative value here is refused and the card falls back to its
 * normal internal route.
 */
export const PRODUCT_EXTERNAL_CTA: Record<string, string> = {
  csp: 'https://csp.yada.hk',
}

/**
 * The route a product card should link to.
 *
 * Falls back to `/products/${slug}` — correct for every product whose
 * slug IS its URL.
 */
export function productRoute(slug: string): string {
  return PRODUCT_ROUTES[slug] ?? `/products/${slug}`
}

/**
 * True for products served as standalone static HTML rather than by React
 * Router — `bite-learn` only. Those must be reached with a plain <a> so the
 * browser hits the server (nginx resolves the directory's index.html); a
 * <Link> would let React Router intercept the navigation and 404 it.
 *
 * Every other product — including `national-education` and the five that
 * used to have no page (hackathon, exploration-trip, mentorship,
 * flash-cards, claw-machine) — is a React route now, so it returns false
 * and its card goes through <Link>.
 */
export function isStaticProductPage(slug: string): boolean {
  return slug === 'bite-learn'
}

/**
 * The OFF-SITE call to action for a product, or null when the product has
 * none — or when its stored URL fails validation.
 *
 * A null return means "carry on as before": the card's button links to the
 * product's own page on this site, exactly like every other product.
 */
export function productExternalCta(slug: string): string | null {
  const raw = PRODUCT_EXTERNAL_CTA[slug]
  if (!raw) return null
  return safeExternalUrl(raw) ?? null
}
